import { useState } from "react";
import type { ChargeLayer } from "@/pages/sy/models/ChargeLayer";
import {
  LAYER_MOVE_MODE,
  type LayerMoveMode,
} from "@/pages/sy/models/LayerMoveMode";
import { useReorderChargeLayerMutation } from "@/pages/sy/hooks/queries/useChargeMutations";
import { getLayerMovePreview } from "@/pages/sy/utils/layerGroups";

type PendingMove = {
  layer: ChargeLayer;
  targetLayerNumber: number;
};

type Params = {
  layers: ChargeLayer[];
  setNotificationMessage: (message: string) => void;
  onError: (err: unknown) => void;
};

export const useChargeLayerMove = ({
  layers,
  setNotificationMessage,
  onError,
}: Params) => {
  const reorderLayerMutation = useReorderChargeLayerMutation();
  const [pendingMove, setPendingMove] = useState<PendingMove | null>(null);

  const executeMove = (
    layer: ChargeLayer,
    layerNumber: number,
    mode: LayerMoveMode,
  ) => {
    if (!layer.ChargeLayerId) return;

    reorderLayerMutation.mutate(
      {
        chargeLayerId: layer.ChargeLayerId,
        newLayerNumber: layerNumber,
        mode,
      },
      {
        onSuccess: () => {
          setNotificationMessage(
            mode === LAYER_MOVE_MODE.MOVE_INTO
              ? `${layer.CommodityName ?? "Material"} moved into layer ${layerNumber}`
              : `Layer reordered to position ${layerNumber}`,
          );
        },
        onError,
        onSettled: () => setPendingMove(null),
      },
    );
  };

  const requestMove = (layer: ChargeLayer, targetLayerNumber: number) => {
    if (targetLayerNumber === layer.LayerNumber) return;

    const targetExists = layers.some(
      (other) =>
        other.LayerNumber === targetLayerNumber &&
        other.ChargeLayerId !== layer.ChargeLayerId,
    );

    if (!targetExists) {
      executeMove(layer, targetLayerNumber, LAYER_MOVE_MODE.REORDER);
      return;
    }

    setPendingMove({ layer, targetLayerNumber });
  };

  const preview = pendingMove
    ? getLayerMovePreview(
        layers,
        pendingMove.layer,
        pendingMove.targetLayerNumber,
        "ChargeLayerId",
      )
    : null;

  const dialogProps = {
    open: pendingMove !== null,
    materialName: pendingMove?.layer.CommodityName ?? "",
    targetLayerNumber: pendingMove?.targetLayerNumber ?? 0,
    targetMaterialNames:
      preview?.targetMaterials.map((layer) => layer.CommodityName ?? "") ?? [],
    reorderLayerNumber: pendingMove?.targetLayerNumber ?? 0,
    moveIntoLayerNumber: preview?.moveIntoLayerNumber ?? 0,
    isCommodityInTarget: preview?.isCommodityInTarget ?? false,
    isPending: reorderLayerMutation.isPending,
    onReorder: () =>
      pendingMove &&
      executeMove(
        pendingMove.layer,
        pendingMove.targetLayerNumber,
        LAYER_MOVE_MODE.REORDER,
      ),
    onMoveInto: () =>
      pendingMove &&
      executeMove(
        pendingMove.layer,
        pendingMove.targetLayerNumber,
        LAYER_MOVE_MODE.MOVE_INTO,
      ),
    onClose: () => setPendingMove(null),
  };

  return { requestMove, dialogProps };
};
